// Mission tab — durable goal-loop objective + a reviewable, unordered plan
// board (`mission_criteria` fact). The agent owns its mutable task list; the
// host only marks plan criteria when turn evidence verifies them.
import { useClient } from '@cortex/react-query';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { formatRelative } from '../../lib/format';
import type { IWatchFact } from '../../lib/use-persistent-agent-watch';

interface ICurrentMission {
  objective: string;
  status: string;
  runsUsed: number | null;
  maxRuns: number | null;
  startedAt: string | null;
  updatedAt: string | null;
  pausedReason?: string;
  activeStepId?: string;
  activeStep?: string;
  stepsDone?: number;
  stepsTotal?: number;
}

interface IMissionHistoryItem {
  objective: string;
  outcome: 'done' | 'exhausted';
  reason?: string;
  at: string;
}

interface IMissionHistoryData {
  items: IMissionHistoryItem[];
}

interface IEditableCriterion {
  id?: string;
  content: string;
  required: boolean;
}

interface IMissionCriterionItem {
  id: string;
  content: string;
  required: boolean;
  status: 'pending' | 'active' | 'completed' | 'skipped';
  order: number;
  evidence?: string;
  completedAt?: string;
}

interface IMissionCriteriaFact {
  objective: string;
  items: IMissionCriterionItem[];
  done: number;
  total: number;
  activeId: string | null;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isCurrentMission(data: unknown): data is ICurrentMission {
  return (
    isPlainRecord(data) &&
    typeof data.objective === 'string' &&
    typeof data.status === 'string' &&
    (typeof data.runsUsed === 'number' || data.runsUsed === null) &&
    (typeof data.maxRuns === 'number' || data.maxRuns === null) &&
    (typeof data.startedAt === 'string' || data.startedAt === null) &&
    (typeof data.updatedAt === 'string' || data.updatedAt === null) &&
    (data.pausedReason === undefined || typeof data.pausedReason === 'string') &&
    (data.activeStep === undefined || typeof data.activeStep === 'string') &&
    (data.activeStepId === undefined || typeof data.activeStepId === 'string') &&
    (data.stepsDone === undefined || typeof data.stepsDone === 'number') &&
    (data.stepsTotal === undefined || typeof data.stepsTotal === 'number')
  );
}

/** True when the mission board still has work the operator can stop. */
function isStoppableMission(
  current: ICurrentMission | null,
  liveCriteria: IMissionCriterionItem[],
): boolean {
  if (liveCriteria.some((i) => i.status === 'active' || i.status === 'pending')) return true;
  if (!current) return false;
  if (!current.objective.trim()) return false;
  const terminal = new Set(['cancelled', 'done', 'completed', 'exhausted']);
  return !terminal.has(current.status);
}

function isMissionHistoryItem(value: unknown): value is IMissionHistoryItem {
  return (
    isPlainRecord(value) &&
    typeof value.objective === 'string' &&
    (value.outcome === 'done' || value.outcome === 'exhausted') &&
    (value.reason === undefined || typeof value.reason === 'string') &&
    typeof value.at === 'string'
  );
}

function isMissionHistoryData(data: unknown): data is IMissionHistoryData {
  return isPlainRecord(data) && Array.isArray(data.items) && data.items.every(isMissionHistoryItem);
}

function isMissionCriteriaFact(data: unknown): data is IMissionCriteriaFact {
  if (!isPlainRecord(data) || !Array.isArray(data.items)) return false;
  if (typeof data.objective !== 'string') return false;
  if (typeof data.done !== 'number' || typeof data.total !== 'number') return false;
  if (data.activeId !== null && typeof data.activeId !== 'string') return false;
  return data.items.every((item) => {
    if (!isPlainRecord(item)) return false;
    return (
      typeof item.id === 'string' &&
      typeof item.content === 'string' &&
      typeof item.required === 'boolean' &&
      typeof item.order === 'number' &&
      (item.status === 'pending' ||
        item.status === 'active' ||
        item.status === 'completed' ||
        item.status === 'skipped')
    );
  });
}

/** Green is reserved for a host-verified completed plan criterion. */
export function criterionRowStyle(status: IMissionCriterionItem['status']): {
  opacity: number;
  borderColor: string;
  background: string;
} {
  if (status === 'completed') {
    return {
      opacity: 0.85,
      borderColor: 'rgba(62, 207, 142, 0.35)',
      background: 'rgba(62, 207, 142, 0.06)',
    };
  }
  if (status === 'active') {
    return {
      opacity: 1,
      borderColor: 'rgba(232, 171, 127, 0.45)',
      background: 'rgba(232, 171, 127, 0.08)',
    };
  }
  return {
    opacity: 0.55,
    borderColor: 'rgba(255,255,255,0.08)',
    background: 'transparent',
  };
}

const outcomeBadge: Record<IMissionHistoryItem['outcome'], string> = {
  done: 'badge badge--success',
  exhausted: 'badge badge--warning',
};

/** Mission tab — set/retask objective + optional plan board, current board, history. */
export function MissionPanel({
  facts,
  agentId,
  threadId,
}: {
  facts: IWatchFact[];
  agentId: string;
  threadId: string;
}) {
  const client = useClient();
  const [objective, setObjective] = useState('');
  const [maxRunsInput, setMaxRunsInput] = useState('');
  const [criteria, setCriteria] = useState<IEditableCriterion[]>([]);
  const [decomposed, setDecomposed] = useState(false);

  const maxRunsTrimmed = maxRunsInput.trim();
  // Mirrors SetPersistentAgentObjectiveInputSchema: maxRuns is an optional
  // 1-200 integer — blank means "omit it" (inherit the base agent's budget).
  const maxRunsValid =
    maxRunsTrimmed === '' ||
    (/^\d+$/.test(maxRunsTrimmed) && Number(maxRunsTrimmed) >= 1 && Number(maxRunsTrimmed) <= 200);

  const decompose = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/objective/decompose`, {
          json: { objective: objective.trim() },
        })
        .json<{ criteria: Array<{ content: string; required: boolean }> }>(),
    onSuccess: (data) => {
      setCriteria(
        (data.criteria ?? []).map((c) => ({
          content: c.content,
          required: c.required !== false,
        })),
      );
      setDecomposed(true);
    },
  });

  const setMission = useMutation({
    mutationFn: () => {
      const cleaned = criteria
        .map((c) => ({
          ...(c.id ? { id: c.id } : {}),
          content: c.content.trim(),
          required: c.required,
        }))
        .filter((c) => c.content.length > 0);
      return client.http
        .put(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/objective`, {
          json: {
            objective: objective.trim(),
            maxRuns: maxRunsTrimmed === '' ? undefined : Number(maxRunsTrimmed),
            ...(cleaned.length > 0
              ? { criteria: cleaned, autoDecompose: false }
              : { autoDecompose: false }),
          },
        })
        .json();
    },
    onSuccess: () => {
      setObjective('');
      setMaxRunsInput('');
      setCriteria([]);
      setDecomposed(false);
    },
  });

  const stopMission = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/objective/interrupt`)
        .json(),
  });

  const canSubmit = objective.trim().length > 0 && maxRunsValid;

  const currentFact = facts.find((f) => f.laneId === 'mission');
  const historyFact = facts.find((f) => f.laneId === 'missions');
  const criteriaFact = facts.find((f) => f.laneId === 'mission_criteria');
  const current = currentFact && isCurrentMission(currentFact.data) ? currentFact.data : null;
  const liveCriteria =
    criteriaFact && isMissionCriteriaFact(criteriaFact.data) && criteriaFact.data.items.length > 0
      ? [...criteriaFact.data.items].sort((a, b) => a.order - b.order)
      : [];
  const history =
    historyFact && isMissionHistoryData(historyFact.data)
      ? [...historyFact.data.items].sort(
          (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
        )
      : [];
  const canStopMission = isStoppableMission(current, liveCriteria);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div
          className="t-xs t-muted"
          style={{ textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}
        >
          Set / retask mission
        </div>
        <label className="field-label" htmlFor="pa-mission-objective">
          Objective — what the thread pursues until retasked
        </label>
        <textarea
          id="pa-mission-objective"
          className="input"
          placeholder="e.g. Go to dock 4, then pick package P-1, then deliver to zone B"
          value={objective}
          onChange={(e) => {
            setObjective(e.target.value);
            setMission.reset();
            decompose.reset();
          }}
          rows={3}
          // `.input` pins height:36px (built for single-line) — override so a
          // 3-row textarea actually renders 3 rows and stays resizable.
          style={{ resize: 'vertical', height: 'auto', minHeight: 76 }}
        />
        <div
          style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}
        >
          <span className="field-label" style={{ marginBottom: 0 }}>
            Max runs
          </span>
          <input
            className="input"
            type="number"
            min={1}
            max={200}
            placeholder="inherit"
            value={maxRunsInput}
            onChange={(e) => {
              setMaxRunsInput(e.target.value);
              setMission.reset();
            }}
            style={{ width: 80 }}
          />
          <button
            className="btn btn--sm"
            disabled={!objective.trim() || decompose.isPending}
            onClick={() => decompose.mutate()}
            title="Generate a reviewable plan draft without starting the mission. Criteria may complete in any order."
          >
            {decompose.isPending ? 'Planning…' : 'Generate plan draft'}
          </button>
          <button
            className="btn btn--primary btn--sm"
            disabled={!canSubmit || setMission.isPending}
            onClick={() => setMission.mutate()}
            style={{ marginLeft: 'auto' }}
            title={
              current
                ? 'Interrupts the active run and replaces the current mission with this one (runs reset to 0).'
                : 'Starts a durable mission the goal judge scores each iteration.'
            }
          >
            {current ? 'Replace mission' : 'Start mission'}
          </button>
        </div>

        {(decomposed || criteria.length > 0) && (
          <div style={{ marginTop: 10 }}>
            <div
              className="t-xs t-muted"
              style={{ textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}
            >
              Plan criteria {decomposed ? '(edit before start)' : ''}
            </div>
            {criteria.map((c, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'flex-start',
                  marginBottom: 6,
                }}
              >
                <span className="t-xs t-mono t-muted" style={{ width: 18, marginTop: 8 }}>
                  {index + 1}.
                </span>
                <input
                  className="input"
                  value={c.content}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCriteria((prev) =>
                      prev.map((row, i) => (i === index ? { ...row, content: value } : row)),
                    );
                  }}
                  style={{ flex: 1 }}
                />
                <label
                  className="t-xs"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}
                >
                  <input
                    type="checkbox"
                    checked={c.required}
                    onChange={(e) => {
                      const required = e.target.checked;
                      setCriteria((prev) =>
                        prev.map((row, i) => (i === index ? { ...row, required } : row)),
                      );
                    }}
                  />
                  req
                </label>
                <button
                  className="btn btn--sm"
                  type="button"
                  onClick={() => setCriteria((prev) => prev.filter((_, i) => i !== index))}
                  title="Remove criterion"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => setCriteria((prev) => [...prev, { content: '', required: true }])}
            >
              + Add criterion
            </button>
          </div>
        )}

        <p
          className="t-xs"
          style={{
            marginTop: 6,
            marginBottom: 0,
            color:
              setMission.isError || decompose.isError
                ? 'var(--danger, #e5484d)'
                : 'var(--text-muted)',
          }}
        >
          {setMission.isError
            ? `Failed to set mission: ${(setMission.error as Error)?.message ?? 'unknown error'}`
            : decompose.isError
              ? `Failed to generate plan: ${(decompose.error as Error)?.message ?? 'unknown error'}`
              : setMission.isSuccess
                ? 'Mission started — plan criteria can complete independently from turn evidence.'
                : 'Optional: generate and edit a plan draft, then start. Without a plan, the full mission begins directly. The agent task list is its live work plan.'}
        </p>
      </div>
      {!current && history.length === 0 && liveCriteria.length === 0 && (
        <span className="t-muted t-sm">No mission recorded yet.</span>
      )}
      {(current || liveCriteria.length > 0) && (
        <div>
          <div
            className="t-xs t-muted"
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>Current mission</span>
            {canStopMission && (
              <button
                className="btn btn--sm"
                disabled={stopMission.isPending}
                onClick={() => stopMission.mutate()}
                title="Stop the mission: abort the active run, clear the plan board, and cancel pending self-wakes."
                style={{ color: 'var(--danger, #e5484d)', borderColor: 'var(--danger, #e5484d)' }}
              >
                {stopMission.isPending ? 'Stopping…' : '⏹ Stop mission'}
              </button>
            )}
          </div>
          {stopMission.isError && (
            <p className="t-xs" style={{ color: 'var(--danger, #e5484d)', marginTop: 0 }}>
              Failed to stop: {(stopMission.error as Error)?.message ?? 'unknown error'}
            </p>
          )}
          {stopMission.isSuccess && (
            <p className="t-xs t-muted" style={{ marginTop: 0 }}>
              Mission stopped — objective, plan, and pending self-wakes cleared.
            </p>
          )}
          {current && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(232,171,127,0.06)',
                border: '1px solid rgba(232,171,127,0.15)',
                borderRadius: 'var(--radius-sm, 3px)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span className="badge badge--neutral t-mono">{current.status}</span>
                {currentFact && (
                  <span className="t-xs t-muted">{formatRelative(currentFact.date)}</span>
                )}
              </div>
              <div className="t-sm" style={{ marginTop: 6 }}>
                {current.objective}
              </div>
              {current.maxRuns != null && (
                <div style={{ marginTop: 8 }}>
                  <div className="t-xs t-muted">
                    {current.runsUsed ?? 0}/{current.maxRuns} runs
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      height: 4,
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 2,
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: `${Math.min(((current.runsUsed ?? 0) / current.maxRuns) * 100, 100)}%`,
                        background: 'var(--terminal-amber)',
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="t-xs t-muted" style={{ marginTop: 8 }}>
                started {formatRelative(current.startedAt)} · updated{' '}
                {formatRelative(current.updatedAt)}
              </div>
              {current.pausedReason && (
                <div className="t-xs" style={{ marginTop: 6, color: 'var(--danger, #e5484d)' }}>
                  {current.status === 'cancelled' ? 'stopped' : 'paused'}: {current.pausedReason}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {liveCriteria.length > 0 && (
        <div>
          <div
            className="t-xs t-muted"
            style={{ textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}
          >
            Plan criteria
            {criteriaFact && isMissionCriteriaFact(criteriaFact.data)
              ? ` · ${criteriaFact.data.done}/${criteriaFact.data.total}`
              : ''}
          </div>
          {liveCriteria.map((item) => {
            const style = criterionRowStyle(item.status);
            const checked = item.status === 'completed';
            return (
              <div
                key={item.id}
                style={{
                  padding: '8px 10px',
                  marginBottom: 6,
                  borderRadius: 'var(--radius-sm, 3px)',
                  border: `1px solid ${style.borderColor}`,
                  background: style.background,
                  opacity: style.opacity,
                }}
              >
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={true}
                    readOnly={true}
                    style={{ marginTop: 3 }}
                    title={
                      item.status === 'active'
                        ? 'Open — this criterion may complete independently when the host verifies evidence'
                        : item.status === 'skipped'
                          ? 'Skipped — not verified complete'
                          : item.status
                    }
                  />
                  <div style={{ flex: 1 }}>
                    <div className="t-sm">
                      <span className="t-mono t-xs t-muted" style={{ marginRight: 6 }}>
                        {item.id}
                      </span>
                      {item.content}
                      {!item.required && (
                        <span className="t-xs t-muted" style={{ marginLeft: 6 }}>
                          optional
                        </span>
                      )}
                      {item.status === 'active' && (
                        <span className="badge badge--warning" style={{ marginLeft: 6 }}>
                          open
                        </span>
                      )}
                      {item.status === 'skipped' && (
                        <span className="badge badge--neutral" style={{ marginLeft: 6 }}>
                          skipped
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div
            className="t-xs t-muted"
            style={{ textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}
          >
            History
          </div>
          {history.map((m, i) => (
            <div
              key={i}
              style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span className={outcomeBadge[m.outcome]}>{m.outcome}</span>
                <span className="t-xs t-muted">{formatRelative(m.at)}</span>
              </div>
              <div className="t-sm" style={{ marginTop: 4 }}>
                {m.objective}
              </div>
              {m.reason && (
                <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                  {m.reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
